/**
 * Team.jsx
 * Who else uses Kreeboom, and what they can do.
 *
 * The first thing on the screen is the thing that would otherwise waste a
 * week: adding someone here does not let them in. Cloudflare Access holds the
 * allow-list for the door; this list only decides what a person may do once
 * they are through it. Both steps, every time.
 *
 * The activity log sits underneath because it is the same subject seen from
 * the other end — who has done what, which is the question that gets asked
 * after something goes out that nobody remembers approving.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Users, Warning, Check, Plus, Eye } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

export default function Team() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.team.get());
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function act(fn, message) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await fn();
      setNote(message ?? res?.message ?? res?.reminder ?? null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function showActivity() {
    if (activity) {
      setActivity(null);
      return;
    }
    try {
      const res = await api.team.activity({ days: 30 });
      setActivity(res.activity);
    } catch (e) {
      setError(e);
    }
  }

  const canAdmin = data?.me?.can?.includes('admin');

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Team</div>
          <h1>Who can do what</h1>
          <p>
            Kreeboom decides what someone can do. Cloudflare Access decides whether they can get in
            at all — and that is a separate list.
          </p>
        </div>
        {canAdmin && (
          <div className="page-head__actions">
            <button className="btn btn--primary" onClick={() => setAdding(true)}>
              <Plus /> Add someone
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--ok">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {!data ? (
        <div className="card">
          <p className="hint">Loading…</p>
        </div>
      ) : (
        <>
          {/* The thing that would otherwise waste a week. */}
          <div className="notice notice--info">
            <Warning />
            <span>{data.note}</span>
          </div>

          {!canAdmin && data.me && (
            <div className="card">
              <div className="card__head">
                <div className="card__head-text">
                  <h2>You are {data.me.roleLabel.toLowerCase()}</h2>
                  <p>{data.me.roleDescription}</p>
                </div>
              </div>
            </div>
          )}

          <People data={data} act={act} busy={busy} canAdmin={canAdmin} />

          {adding && (
            <AddPerson
              data={data}
              onClose={() => setAdding(false)}
              onAdded={(reminder) => {
                setAdding(false);
                setNote(reminder);
                load();
              }}
            />
          )}

          <Roles data={data} />

          <section className="card">
            <div className="card__head">
              <span className="tile">
                <Eye size={21} />
              </span>
              <div className="card__head-text">
                <h2>What has been done</h2>
                <p>Every change, who made it and when. Kept for two years.</p>
              </div>
            </div>

            <button className="btn" onClick={showActivity}>
              {activity ? 'Hide' : 'Show the last 30 days'}
            </button>

            {activity && (
              <div className="rows" style={{ marginTop: 14 }}>
                {activity.length === 0 ? (
                  <p className="hint">Nothing recorded yet.</p>
                ) : (
                  activity.map((row) => (
                    <div className="row" key={row.id}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="row__label">{row.detail || row.action}</div>
                        <div className="row__sub">
                          {row.actor || 'unknown'} · {formatLondon(row.at)}
                        </div>
                      </div>
                      <div className="row__end">
                        <span className="badge">{row.subject || row.action}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

function People({ data, act, busy, canAdmin }) {
  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Users size={21} />
        </span>
        <div className="card__head-text">
          <h2>
            {data.users.length} {data.users.length === 1 ? 'person' : 'people'}
          </h2>
          <p>{canAdmin ? 'You can change any of these.' : 'Only an owner can change these.'}</p>
        </div>
      </div>

      <div className="rows">
        {data.users.map((u) => {
          const isMe = u.email === data.me?.email;
          return (
            <div className={`row${u.disabled ? ' row--off' : ''}`} key={u.email}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="row__label">
                  {u.name || u.email}
                  {isMe && <span className="pill pill--muted">you</span>}
                </div>
                <div className="row__sub">
                  {u.name ? `${u.email} · ` : ''}
                  {u.last_seen_at ? `last here ${formatLondon(u.last_seen_at)}` : 'never signed in'}
                  {u.brands ? ` · ${u.brands.length} brand${u.brands.length === 1 ? '' : 's'} only` : ''}
                </div>
              </div>

              <div className="row__end">
                {u.disabled && <span className="badge">switched off</span>}

                {canAdmin && !isMe ? (
                  <select
                    value={u.role}
                    disabled={busy}
                    onChange={(e) => act(() => api.team.update(u.email, { role: e.target.value }))}
                  >
                    {data.roles.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="badge badge--accent">{u.roleLabel}</span>
                )}

                {canAdmin && !isMe && (
                  <>
                    <button
                      className="btn btn--sm"
                      disabled={busy}
                      onClick={() => act(() => api.team.update(u.email, { disabled: !u.disabled }))}
                    >
                      {u.disabled ? 'Switch on' : 'Switch off'}
                    </button>
                    <button
                      className="btn btn--sm btn--danger"
                      disabled={busy}
                      onClick={() => act(() => api.team.remove(u.email))}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AddPerson({ data, onClose, onAdded }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('editor');
  const [brandIds, setBrandIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const chosen = data.roles.find((r) => r.key === role);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.team.add({ email: email.trim(), name: name.trim(), role, brandIds });
      onAdded(res.reminder);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>
          <Plus size={19} /> Add someone
        </h2>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="t-email">Email</label>
          <input
            id="t-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@archevot.com"
          />
          <p className="hint">The address they sign in to Cloudflare Access with. It has to match.</p>
        </div>

        <div className="field">
          <label htmlFor="t-name">Name (optional)</label>
          <input id="t-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="t-role">Role</label>
          <select id="t-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {data.roles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="hint">{chosen?.description}</p>
        </div>

        {data.brands.length > 1 && (
          <div className="field">
            <label>Limit to certain brands (optional)</label>
            <div className="chips">
              {data.brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className={`chip-btn${brandIds.includes(b.id) ? ' is-on' : ''}`}
                  onClick={() =>
                    setBrandIds((ids) =>
                      ids.includes(b.id) ? ids.filter((id) => id !== b.id) : [...ids, b.id],
                    )
                  }
                >
                  {b.name}
                </button>
              ))}
            </div>
            <p className="hint">
              {brandIds.length === 0
                ? 'Nothing chosen means every brand.'
                : `Only ${brandIds.length} of ${data.brands.length}.`}
            </p>
          </div>
        )}

        <div className="btn-row">
          <button className="btn btn--primary" onClick={add} disabled={busy || !email.trim()}>
            {busy ? 'Adding…' : 'Add them'}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>

        <p className="hint" style={{ marginTop: 12 }}>
          Then add the same address to the Cloudflare Access policy for this application, or they
          will never get past the sign-in page.
        </p>
      </div>
    </div>
  );
}

function Roles({ data }) {
  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>What each role means</h2>
        </div>
      </div>

      <div className="rows">
        {data.roles.map((r) => (
          <div className="row" key={r.key}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="row__label">{r.label}</div>
              <div className="row__sub">{r.description}</div>
            </div>
            <div className="row__end">
              {r.can.map((c) => (
                <span className="badge" key={c}>
                  {c}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
